import { Calendar, Save, CheckCircle2, AlertCircle, Send, RotateCcw } from "lucide-react";
import { SafeHtml } from "@/lib/safeHtml";
import useMonthlyDraft from "@/hooks/useMonthlyDraft";

const STATUS_META = {
  draft: { label: "Borrador", color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertCircle },
  approved: { label: "Aprobada · se enviará sola", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  sent: { label: "Enviada ✓", color: "bg-sage/20 text-charcoal border-sage/40", icon: Send },
};

/**
 * Persistent monthly draft block in the admin dashboard.
 *
 * Marta picks a target month (YYYY-MM), edits subject + body, and toggles
 * approval. The cron at day 1 09:00 Europe/Madrid picks it up if approved.
 */
const MonthlyDraft = ({ password, loggedIn }) => {
  const d = useMonthlyDraft(password, loggedIn);
  if (!loggedIn) return null;

  const statusKey = d.draft?.status || "draft";
  const status = STATUS_META[statusKey] || STATUS_META.draft;
  const StatusIcon = status.icon;
  const isSent = statusKey === "sent";

  return (
    <div
      className="bg-white rounded-3xl border border-sand/50 mt-10 overflow-hidden"
      data-testid="monthly-draft"
    >
      <div className="p-7 border-b border-sand/40">
        <div className="flex items-center gap-3 flex-wrap">
          <Calendar size={18} className="text-terracotta" />
          <h2 className="font-serif text-2xl text-charcoal">Newsletter mensual automática</h2>
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full border ${status.color}`}
            data-testid="monthly-status"
          >
            <StatusIcon size={12} /> {status.label}
          </span>
        </div>
        <p className="text-sm text-charcoal/60 mt-3 leading-relaxed">
          Escribe el borrador, guárdalo, y cuando esté listo pulsa <strong>"Aprobar"</strong>. El
          día <strong>1 del mes objetivo a las 09:00</strong> se envía sola a todas tus suscriptoras.
          Recibirás un recordatorio el día 25 cada mes.
        </p>
      </div>

      <div className="p-7 space-y-5">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="text-[11px] tracking-[0.28em] uppercase text-charcoal/60">Mes objetivo</label>
          <input
            type="month"
            value={d.targetMonth}
            onChange={(e) => d.setTargetMonth(e.target.value)}
            data-testid="monthly-target-input"
            className="px-3 py-2 rounded-lg border border-sand/80 focus:border-terracotta focus:ring-1 focus:ring-terracotta outline-none text-charcoal text-sm"
          />
          <span className="text-[14px] text-charcoal/60 italic">
            → <strong className="text-charcoal not-italic">{d.monthLabel}</strong>
          </span>
          {d.loading && <span className="text-xs text-charcoal/50 italic">cargando…</span>}
        </div>

        <div>
          <label className="text-[11px] tracking-[0.28em] uppercase text-charcoal/60 mb-2 block">Asunto</label>
          <input
            type="text"
            value={d.subject}
            onChange={(e) => d.setSubject(e.target.value)}
            disabled={isSent}
            placeholder={`Newsletter de ${d.monthLabel}`}
            data-testid="monthly-subject-input"
            className="w-full px-4 py-3 rounded-xl border border-sand/80 focus:border-terracotta focus:ring-1 focus:ring-terracotta outline-none text-charcoal disabled:opacity-60 disabled:bg-sand/30"
          />
        </div>

        <div>
          <label className="text-[11px] tracking-[0.28em] uppercase text-charcoal/60 mb-2 block">Cuerpo (HTML · usa {"{name}"} para personalizar)</label>
          <textarea
            rows={14}
            value={d.bodyHtml}
            onChange={(e) => d.setBodyHtml(e.target.value)}
            disabled={isSent}
            data-testid="monthly-body-textarea"
            className="w-full px-4 py-3 rounded-xl border border-sand/80 focus:border-terracotta focus:ring-1 focus:ring-terracotta outline-none text-charcoal font-mono text-[13px] leading-relaxed disabled:opacity-60 disabled:bg-sand/30"
          />
        </div>

        {d.bodyHtml && (
          <details className="border border-sand/60 rounded-xl bg-cream/40">
            <summary className="cursor-pointer px-5 py-3 text-[11px] tracking-[0.28em] uppercase text-charcoal/65 select-none">
              Vista previa
            </summary>
            <SafeHtml className="prose prose-sm max-w-none text-charcoal/85 px-5 pb-5" html={d.bodyHtml.replace(/{name}/g, "Marta")} />
          </details>
        )}

        {!isSent && (
          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={d.save}
              disabled={d.saving}
              data-testid="monthly-save-btn"
              className="inline-flex items-center gap-2 border border-charcoal/20 text-charcoal rounded-full px-5 py-2.5 text-sm hover:border-charcoal/50 transition-colors disabled:opacity-50"
            >
              <Save size={15} /> {d.saving ? "Guardando…" : "Guardar borrador"}
            </button>
            <button
              onClick={d.toggleApproval}
              disabled={d.saving || !d.draft}
              data-testid="monthly-approve-btn"
              className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                statusKey === "approved"
                  ? "bg-amber-600 text-ivory hover:bg-amber-700"
                  : "bg-emerald-600 text-ivory hover:bg-emerald-700"
              }`}
            >
              {statusKey === "approved" ? (
                <><RotateCcw size={15} /> Desaprobar (volver a borrador)</>
              ) : (
                <><CheckCircle2 size={15} /> Aprobar para envío automático</>
              )}
            </button>
          </div>
        )}

        {isSent && d.draft?.sent_at && (
          <p className="text-sm text-charcoal/65 bg-sage/10 border border-sage/30 rounded-xl px-5 py-3.5">
            ✓ Esta newsletter se envió el {new Date(d.draft.sent_at).toLocaleString("es-ES", { dateStyle: "long", timeStyle: "short" })}. El borrador queda archivado como referencia.
          </p>
        )}
      </div>
    </div>
  );
};

export default MonthlyDraft;
